import { AxiosResponse } from 'axios'
import { getBoardCasesApi } from 'Modules/Boardwork/Meetings/shared/api/boardCases.api'
import {
  IBoardCase,
  IBoardCasesList,
} from 'Modules/Boardwork/Meetings/shared/models/BoardCase'
import {
  patchEmail,
  patchEmailById,
} from 'Modules/Communication/Email/shared/api/emails.api'
import { getEmail as getEmailApi } from 'Modules/Communication/Email/shared/api/emails.api'
import { IEmailModel } from 'Modules/Communication/Email/shared/models/Email'
import { connectMessagesToCase } from 'Modules/Communication/shared/api/inbox.api'
import {
  IInboxPatchModel,
  IInboxPatchSingle,
} from 'Modules/Communication/shared/models/Inbox'
import { ILabel } from 'Modules/Communication/shared/models/Labels'
import {
  patchSms,
  patchSmsById,
} from 'Modules/Communication/Sms/shared/api/sms.api'
import { ISmsModel } from 'Modules/Communication/Sms/shared/models/Sms'
import { put, takeEvery, select, call } from 'redux-saga/effects'
import { IPatchModel } from 'shared/models/Common'
import { navigate } from 'shared/routing'
import {
  EMAILS_FETCH_STATISTICS_REQUESTED,
  EMAILS_REMOVE_FROM_LIST,
  EMAIL_REMOVE_LABEL,
  EMAIL_SET_LABELS,
  EMAIL_SET_SINGLE_LIST_ACTION_ID,
} from 'shared/store/communication/emails/actionTypes'
import {
  getCurrentEmail,
  getEmailsMarkedForActions,
  getSingleListActionId as getSingleListEmailActionId,
} from 'shared/store/communication/emails/selectors'
import { getInboxLabels } from 'shared/store/communication/labels/selectors'
import {
  SMS_REMOVE_FROM_LIST,
  SMS_REMOVE_LABEL,
  SMS_SET_LABELS,
  SMS_SET_SINGLE_LIST_ACTION_ID,
} from 'shared/store/communication/smsMessages/actionTypes'
import {
  getCurrentSms,
  getSingleListSmsActionId,
  getSmsMessagesMarkedForActions,
} from 'shared/store/communication/smsMessages/selectors'
import { closeModal } from 'shared/store/modal'
import { addToasterMessage } from 'shared/store/toaster'
import { dynamicSort } from 'shared/utils/array-helpers'
import {
  INBOX_ADD_TO_CASE_REQUESTED,
  INBOX_ADD_TO_CASE_SUCCESS,
  INBOX_FETCH_CASES_REQUESTED,
  INBOX_FETCH_CASES_SUCCESS,
  INBOX_FETCH_CASES_FAILED,
  INBOX_ADD_TO_CASE_FAILED,
  INBOX_REMOVE_LABEL_SUCCESS,
  INBOX_REMOVE_LABEL_ERROR,
  INBOX_SET_ADDED_LABELS_SUCCESS,
  INBOX_ADD_MARKED_LABELS_REQUESTED,
  INBOX_SET_ADDED_LABELS_REQUESTED,
  INBOX_REMOVE_LABEL_REQUESTED,
  INBOX_ADD_MARKED_LABELS_SUCCESS,
  INBOX_RESET_MARKED_LABELS,
  INBOX_ADD_MARKED_LABELS_ERROR,
  INBOX_SET_FOR_CASE_REQUESTED,
  INBOX_SET_FOR_CASE_SUCCESS,
  INBOX_SET_FOR_CASE_ERROR,
} from './actionTypes'
import { getInboxMarkedLabels } from './selectors'
import { InboxActionTypes } from './types'

function* fetchCases(action: InboxActionTypes) {
  try {
    const { data }: AxiosResponse<IBoardCasesList> = yield call(
      getBoardCasesApi,
      action.payload
    )
    yield put({
      type: INBOX_FETCH_CASES_SUCCESS,
      payload: data?.result?.sort(dynamicSort<IBoardCase>('title')),
    })
  } catch (e) {
    yield put({
      type: INBOX_FETCH_CASES_FAILED,
      payload: 'Det skjedde en feil ved uthenting av saker',
    })
  }
}

function* addCaseToMessages(action: InboxActionTypes) {
  try {
    const isSms = action.payload.isSms
    const current: string = isSms
      ? yield select(getCurrentSms)
      : yield select(getCurrentEmail)
    const marked: string[] = isSms
      ? yield select(getSmsMessagesMarkedForActions)
      : yield select(getEmailsMarkedForActions)

    const ids = marked?.length ? marked : [current]

    if (!action.payload.isNewCase) {
      yield call(connectMessagesToCase, action.payload.caseId, ids)
    }

    if (action.payload.redirect && current) {
      // Redirect from message details
      navigate.push(action.payload.redirect)
    }

    if (!action.payload.isProcessedFolder) {
      yield put({
        type: isSms ? SMS_REMOVE_FROM_LIST : EMAILS_REMOVE_FROM_LIST,
        payload: ids,
      })
    }
    yield put({
      type: INBOX_ADD_TO_CASE_SUCCESS,
      payload: ids,
    })
    yield put({ type: EMAILS_FETCH_STATISTICS_REQUESTED })

    yield put(
      addToasterMessage({
        text: `${isSms ? 'SMS-en' : 'E-posten'}${
          ids.length > 1 ? 'e' : ''
        } ble knyttet til saken og flyttet til mappen "Behandlet"`,
        type: 'success',
      })
    )
  } catch (e) {
    yield put({
      type: INBOX_ADD_TO_CASE_FAILED,
      payload: 'Det skjedde en feil ved knytting til sak',
    })
  }
}

// Handles multiple and single sms
function* updateMarkedLabelsForMessage(action: InboxActionTypes) {
  const isEmail = action.payload?.isEmail
  try {
    const singleListActionId: string | null = isEmail
      ? yield select(getSingleListEmailActionId)
      : yield select(getSingleListSmsActionId)
    const marked: string[] = isEmail
      ? yield select(getEmailsMarkedForActions)
      : yield select(getSmsMessagesMarkedForActions)
    const { data: labelsMarked }: { data: string[] } =
      yield select(getInboxMarkedLabels)

    const payload: IInboxPatchModel = {
      model: [],
    }

    if (marked.length) {
      const model: IInboxPatchSingle[] = marked.map((item) => ({
        id: item,
        patchModel: [
          {
            op: 'add',
            path: '/labelIds',
            value: labelsMarked,
          },
        ],
      }))

      payload.model = model
    } else {
      const current: string = yield select(
        (isEmail ? getCurrentEmail : getCurrentSms) as (state: any) => string
      )

      let patchId = current

      if (singleListActionId) patchId = singleListActionId

      const patchModel: IPatchModel<string[]>[] = [
        {
          op: 'add',
          path: '/labelIds',
          value: labelsMarked,
        },
      ]

      payload.model = [
        {
          id: patchId,
          patchModel,
        },
      ]
    }

    // Call to patch endpoint
    yield call(isEmail ? patchEmail : patchSms, payload)

    yield put({ type: INBOX_ADD_MARKED_LABELS_SUCCESS })
    yield put(closeModal())
    yield put({
      type: INBOX_SET_ADDED_LABELS_REQUESTED,
      payload: { labelIds: labelsMarked, isEmail },
    })
    yield put({ type: INBOX_RESET_MARKED_LABELS })
  } catch (e) {
    console.log(e)
    yield put({
      type: INBOX_ADD_MARKED_LABELS_ERROR,
    })
  } finally {
    yield put({
      type: isEmail
        ? EMAIL_SET_SINGLE_LIST_ACTION_ID
        : SMS_SET_SINGLE_LIST_ACTION_ID,
      payload: null,
    })
  }
}

function* addLabelsToMessage(action: InboxActionTypes) {
  try {
    const isEmail = action.payload.isEmail
    const { data: labels }: { data: ILabel[] } = yield select(getInboxLabels)
    const labelIds: string[] = action.payload.labelIds

    const labelsToAdd: ILabel[] =
      labels.filter((label) => labelIds.includes(label.id)) || []

    yield put({
      type: isEmail ? EMAIL_SET_LABELS : SMS_SET_LABELS,
      payload: labelsToAdd,
    })
    yield put({
      type: INBOX_SET_ADDED_LABELS_SUCCESS,
      payload: labelsToAdd,
    })
  } catch (e) {
    console.log(e)
  }
}

function* removeLabelFromMessage(action: InboxActionTypes) {
  try {
    const isEmail = action.payload.isEmail
    const patchModel: IPatchModel[] = [
      {
        op: 'remove',
        path: '/labelIds',
        value: [action.payload.id],
      },
    ]

    yield call(
      isEmail ? patchEmailById : patchSmsById,
      action.payload.messageId,
      patchModel
    )
    yield put({ type: INBOX_REMOVE_LABEL_SUCCESS, payload: action.payload.id })
    yield put({
      type: isEmail ? EMAIL_REMOVE_LABEL : SMS_REMOVE_LABEL,
      payload: action.payload.id,
    })
  } catch (e) {
    console.log(e)
    yield put({ type: INBOX_REMOVE_LABEL_ERROR, payload: action.payload.id })

    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil under fjerning av etikett',
        type: 'danger',
      })
    )
  }
}

function* handleSetMessageForCase(action: InboxActionTypes) {
  try {
    const { payload } = action
    let message: AxiosResponse<IEmailModel | ISmsModel> | null = null

    // If email and full email not passed, we fetch it (we allways have full sms)
    if (!payload.isSms && !payload.message) {
      message = yield call(getEmailApi, payload.id)
    }

    yield put({
      type: INBOX_SET_FOR_CASE_SUCCESS,
      payload: payload.message || message?.data,
    })
  } catch (e) {
    console.log(e)
    yield put({
      type: INBOX_SET_FOR_CASE_ERROR,
    })
  }
}

function* inboxSaga() {
  yield takeEvery(INBOX_FETCH_CASES_REQUESTED, fetchCases)
  yield takeEvery(INBOX_ADD_TO_CASE_REQUESTED, addCaseToMessages)

  yield takeEvery(
    INBOX_ADD_MARKED_LABELS_REQUESTED,
    updateMarkedLabelsForMessage
  )
  yield takeEvery(INBOX_SET_ADDED_LABELS_REQUESTED, addLabelsToMessage)
  yield takeEvery(INBOX_REMOVE_LABEL_REQUESTED, removeLabelFromMessage)
  yield takeEvery(INBOX_SET_FOR_CASE_REQUESTED, handleSetMessageForCase)
}

export default inboxSaga
